import trackEventMixin from "../trackEventMixin";
import {getWidgetLoadingMode} from "../../services/vueZendeskSvc";
import {mapState, mapMutations} from 'vuex';

export default {
  mixins: [trackEventMixin],
  methods: {
    ...mapMutations('zendesk', [
      'setShowButtonCorner',
      'setFirstClickAction',
      'setIsScriptLoading',
      'setIsButtonClicked',
      'setLoadingMode',
      'setLoadingInterval',
      'setCurrentLoadingProgress',
      'setDisplayLoadingProgress',
      'setLoadingProgressStep',
      'setChatAvailable',
    ]),
    mix_openChat(eventName) {
      if (!eventName || typeof eventName != 'string') {
        eventName = 'CC Chat';
      }

      this.mix_trackEvent(eventName);

      if (window.zendeskByVue) {
        if (this.loadingMode !== 'noLoad') {
          this.mix_onWidgetButtonClick();
        } else {
          window.location.href = "/support";
        }
      } else {
        if (window.zE && typeof window.zE == 'function') {
          window.zE('webWidget', 'show');
          window.zE('webWidget', 'open');
        } else {
          window.location.href = "/support";
        }
      }
    },

    /**
     * Opens the widget if it's loaded, otherwise it loads it and then it opens it
     */
    mix_onWidgetButtonClick() {
      this.setIsButtonClicked(true);

      this.mix_startProgressBar();

      if (this.mix_isMainScriptLoaded()) {
        window.zE('webWidget', 'show');
        window.zE('webWidget', 'open');
        return;
      }

      if (this.isScriptLoading) {
        return;
      }

      this.mix_loadZenDesk();
    },
    /**
     * Loads the script depending on the browser "load" state
     */
    mix_loadZenDesk() {
      if (this.mix_isMainScriptLoaded() || this.isScriptLoading) {
        return;
      }

      this.setIsScriptLoading(true);

      // If the "load" event was already fired, load the script
      if (document.readyState === 'complete') {
        this.mix_loadScript();
      } else {
        // Wait for the "load" event and then load the script
        window.addEventListener('load', this.mix_loadScript);
      }
    },
    /**
     * Inserts and loads the script into the DOM
     */
    mix_loadScript() {
      if (this.mix_isMainScriptLoaded()) {
        return;
      }

      // Create the script
      const script = document.createElement('script');

      // Set Zendesk's script URL and key (prod key)
      script.setAttribute('src', this.mix_zendeskScriptURL);

      // This EXACT ID (ze-snippet) is required for the library to work
      script.setAttribute('id', 'ze-snippet');

      // Register the callback after it's loaded
      script.onload = this.mix_onScriptLoaded;

      // Insert it into the DOM
      document.body.appendChild(script);
    },
    /**
     * Registers show/hide events after the full script was loaded and sets up prefill and event tracking
     */
    mix_onScriptLoaded() {
      window.zE(() => {
        this.mix_setSegmentTrackingEvents();
        this.mix_prefillAndIdentifyCustomer();
        this.mix_setWidgetStatusListeners();

        // Wait until the actual web widget is also loaded
        window.$zopim(() => {
          // Open the chat if the user previously clicked the button
          if (this.isButtonClicked) {
            window.zE('webWidget', 'show');
            window.zE('webWidget', 'open');
          }
        });
      });

      // Hide the "Help / Contact" original Zendesk widget
      window.zE('webWidget', 'hide');
    },
    /**
     * Listens for the open/close widget status
     */
    mix_setWidgetStatusListeners() {
      window.zE('webWidget:on', 'userEvent', (event) => {
        if (event.action === 'Web Widget Opened') {
          this.mix_onWidgetShow();
        }

        if (event.action === 'Web Widget Minimised') {
          this.mix_onWidgetHide();
        }
      });

      // This is necessary for proactive chat to work, since the chat won't automatically open otherwise
      window.$zopim(() => {
        // Open the widget every time a colorist sends a message to the customer
        window.$zopim.livechat.setOnUnreadMsgs(() => {
          window.$zopim.livechat.window.show();
        });
      });
    },
    /**
     * Hides our button when the widget is showing
     */
    mix_onWidgetShow() {
      // Change the loading status after the first time the widget is displayed and loaded
      // This is needed for hiding our button as soon as the widget is displayed the first time
      if (this.firstClickAction) {
        this.setDisplayLoadingProgress(100);
        clearInterval(this.loadingInterval);

        this.setFirstClickAction(false);
      }

      // Hide our button, delay it a little bit so the user will see the "100%" progress
      setTimeout(() => {
        this.setShowButtonCorner(false);
        // This is the only way to know when the entire widget finished loading, when the widget show method is triggered
        this.setIsScriptLoading(false);
        this.mix_afterWidgetShow();
      }, 100);
    },
    /**
     * Hides the original widget button and shows our own when the widget is hidden
     */
    mix_onWidgetHide() {
      // Hide the entire Zendesk widget and widget button
      window.zE('webWidget', 'hide');

      if (this.loadingMode !== 'noLoad') {
        // Show our button
        this.setShowButtonCorner(true);
      }

      this.mix_afterWidgetHide();
    },
    /**
     * Checks if the the main script was already loaded
     * @returns {boolean} status
     */
    mix_isMainScriptLoaded() {
      return window.zE && typeof window.zE == 'function';
    },
    /**
     * Checks and initializes how the script should be loaded
     */
    mix_initialize() {
      this.mix_setWidgetSettings();
      return this.mix_setLoadingMode();
    },
    /**
     * Sets the widgets settings
     */
    mix_setWidgetSettings() {
      window.zendeskByVue = true;
      window.zESettings = {
        webWidget: {
          chat: {
            suppress: false,
            departments: {
              enabled: []
            }
          }
        }
      };
    },
    /**
     * Prefills the widget customer info and identifies it
     */
    mix_prefillAndIdentifyCustomer() {
      const customerData = window.MR.bt_track;
      let identifyParams = {};
      let prefillParams = {};

      if (customerData) {
        if (customerData.email) {
          identifyParams.email = customerData.email;
          prefillParams.email = {
            value: customerData.email,
            readOnly: false // optional
          };
        }

        if (customerData.firstName) {
          identifyParams.name = customerData.firstName;
          prefillParams.name = {
            value: customerData.firstName,
            readOnly: false // optional
          };
          if (customerData.lastName) {
            prefillParams.name.value += ' ' + customerData.lastName;
          }
        }

        if (customerData.phone) {
          prefillParams.phone = {
            value: customerData.phone,
            readOnly: false // optional
          };
        }

        if (Object.keys(identifyParams).length) {
          window.zE('webWidget', 'identify', identifyParams);
        }

        if (Object.keys(prefillParams).length) {
          window.zE('webWidget', 'prefill', prefillParams);
        }
      }
    },

    /**
     * Tracks events when opening/closing the widget in analytics/Segment
     */
    mix_setSegmentTrackingEvents() {
      const eventSettings = [
        {widgetEvent: 'open', segmentEvent: 'Zendesk Widget Opened'},
        {widgetEvent: 'close', segmentEvent: 'Zendesk Widget Closed'},
        {widgetEvent: 'chat:start', segmentEvent: 'Zendesk Chat Started'}
      ];

      for (const eventSetting of eventSettings) {
        window.zE('webWidget:on', eventSetting.widgetEvent, this.mix_trackSegmentEvent.bind(null, eventSetting.segmentEvent));
      }
    },
    /**
     * Tracks an event in Segment
     * @param {string} event
     */
    mix_trackSegmentEvent(event) {
      this.mix_trackEvent(event);
    },
    /**
     * Returns how the script should be loaded: lazy, normal, don't load based on the route
     */
    async mix_getScriptLoadingMode() {
      const url = window.location.href;
      const response = await getWidgetLoadingMode({url});

      // CMS configuration to hide chat from page
      if (window && window.contentData && window.contentData.hideChatWidget) {
        return 'noLoad';
      }
      return (response && response.data && response.data.mode) || 'lazy';
    },
    /**
     * Sets the loading mode for the widget
     */
    async mix_setLoadingMode() {
      const mode = await this.mix_getScriptLoadingMode();

      switch (mode) {
        case 'lazy':
          this.setChatAvailable(true);
          this.setShowButtonCorner(true);
          this.setLoadingMode('lazy');
          break;
        case 'normal':
          this.setChatAvailable(true);
          this.setShowButtonCorner(true);
          this.setLoadingMode('normal');
          this.mix_loadZenDesk();
          break;
        case 'noLoad':
          this.setChatAvailable(false);
          this.setShowButtonCorner(false);
          this.setLoadingMode('noLoad');
          // Close the widget if it was open
          if (this.mix_isMainScriptLoaded()) {
            window.zE('webWidget', 'hide');
          }
          break;
        default:
          this.setShowButtonCorner(true);
          this.setLoadingMode('lazy');
      }
    },
    /**
     * Shows a loading progress from 0% to 100% to improve perceived loading speed
     */
    mix_startProgressBar() {
      if (this.loadingInterval || this.displayLoadingProgress === 100) {
        return;
      }

      this.setLoadingInterval(setInterval(() => {
        if (this.displayLoadingProgress === 100) {
          clearInterval(this.loadingInterval);
          return;
        }

        this.setCurrentLoadingProgress(this.currentLoadingProgress + this.loadingProgressStep);
        const progress = Math.round(Math.atan(this.currentLoadingProgress) / (Math.PI / 2) * 100 * 1000) / 1000;
        this.setDisplayLoadingProgress(Math.trunc(progress));

        if (progress >= 100) {
          clearInterval(this.loadingInterval);
        } else if (progress >= 97) { // Slow down if we're about to reach 100%
          this.setLoadingProgressStep(0.1);
        }
      }, 100));
    },
    /**
     * Updates the answer bot suggestions based on the URL
     */
    mix_updateAnswerBotSuggestions() {
      if (this.mix_isMainScriptLoaded()) {
        window.zE('webWidget', 'helpCenter:setSuggestions', {url: true});
      }
    },
    /**
     * This is a hook for when the widget is fully loaded on site.
     * @returns 
     */
    mix_afterWidgetShow() {
      return;
    },
    /**
     * This is a hook for when the widget is opened again from minimized version.
     * @returns 
     */
    mix_afterWidgetHide() {
      return;
    }
  },
  computed: {
    ...mapState('zendesk', [
      'showButtonCorner',
      'firstClickAction',
      'isScriptLoading',
      'isButtonClicked',
      'loadingMode',
      'loadingInterval',
      'currentLoadingProgress',
      'displayLoadingProgress',
      'loadingProgressStep'
    ]),
    ...mapState('global', [
      'MRConfig',
      'isVueSSRApp'
    ]),
    mix_showLoader() {
      return this.isScriptLoading && this.isButtonClicked;
    },
    // Using this URL as a computed property makes it easier to unit test the feature
    mix_zendeskScriptURL() {
      return `https://static.zdassets.com/ekr/snippet.js?key=${this.isVueSSRApp ? this.MRConfig.zendeskWidgetAppKey : this.$root.MRConfig.zendeskWidgetAppKey}`;
    }
  },
  /**
   * Before destroy life-cycle hook
   */
  beforeDestroy() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
    }

    window.removeEventListener('load', this.mix_loadScript);
  }
};